/**
 * SpinningLogoRenderer - Core WebGL renderer
 */

import { createShaderProgram } from './shaders.js';
import { createMeshFromOutline } from './mesh.js';
import { loadTexture } from './texture.js';
import { AnimationController } from './animations.js';
import { createProjectionMatrix, createModelViewMatrix } from './matrices.js';

export class SpinningLogoRenderer {
  constructor(canvas) {
    this.canvas = canvas;
    this.gl = canvas.getContext('webgl', {
      alpha: true,
      premultipliedAlpha: false,
      antialias: false
    });

    if (!this.gl) {
      console.error('WebGL not supported');
      return;
    }

    this.width = canvas.width;
    this.height = canvas.height;
    this.texture = null;
    this.mesh = null;
    this.shaderProgram = null;
    this.animationController = new AnimationController(2000);

    this.initWebGL();
  }

  initWebGL() {
    const gl = this.gl;

    this.shaderProgram = createShaderProgram(gl);
    if (!this.shaderProgram) {
      console.error('Failed to create shader program');
      return;
    }

    gl.viewport(0, 0, this.width, this.height);
    gl.clearColor(0, 0, 0, 0);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    gl.enable(gl.DEPTH_TEST);
    gl.depthFunc(gl.LESS);

    this.anisotropicExt = gl.getExtension('EXT_texture_filter_anisotropic');
  }

  loadTexture(imageSrc, callback) {
    loadTexture(this.gl, imageSrc, this.anisotropicExt, (texture, width, height) => {
      this.texture = texture;
      this.textureWidth = width;
      this.textureHeight = height;
      if (callback) callback();
    });
  }

  createMesh(outlineData) {
    this.mesh = createMeshFromOutline(this.gl, outlineData, this.width, this.height);
  }

  render() {
    if (!this.texture || !this.mesh || !this.shaderProgram) {
      return;
    }

    const gl = this.gl;
    const { program, attribLocations, uniformLocations } = this.shaderProgram;

    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
    gl.useProgram(program);

    // Set up vertex attributes
    gl.bindBuffer(gl.ARRAY_BUFFER, this.mesh.vertexBuffer);
    gl.enableVertexAttribArray(attribLocations.position);
    gl.vertexAttribPointer(attribLocations.position, 3, gl.FLOAT, false, 0, 0);

    gl.bindBuffer(gl.ARRAY_BUFFER, this.mesh.texCoordBuffer);
    gl.enableVertexAttribArray(attribLocations.texCoord);
    gl.vertexAttribPointer(attribLocations.texCoord, 2, gl.FLOAT, false, 0, 0);

    gl.bindBuffer(gl.ARRAY_BUFFER, this.mesh.normalBuffer);
    gl.enableVertexAttribArray(attribLocations.normal);
    gl.vertexAttribPointer(attribLocations.normal, 3, gl.FLOAT, false, 0, 0);

    // Get current animation state
    const transform = this.animationController.update() || {
      rotationX: 0,
      rotationY: 0,
      rotationZ: 0,
      offsetX: 0,
      offsetY: 0,
      scale: 1
    };

    // Set uniforms
    gl.uniformMatrix4fv(
      uniformLocations.projection,
      false,
      createProjectionMatrix(this.width, this.height)
    );

    gl.uniformMatrix4fv(
      uniformLocations.modelView,
      false,
      createModelViewMatrix(
        transform.rotationX,
        transform.rotationY,
        transform.rotationZ,
        transform.offsetX,
        transform.offsetY,
        transform.scale
      )
    );

    gl.uniform1f(uniformLocations.opacity, 1.0);

    // Bind texture
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.texture);
    gl.uniform1i(uniformLocations.texture, 0);

    // Draw
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.mesh.indexBuffer);
    gl.drawElements(gl.TRIANGLES, this.mesh.indexCount, gl.UNSIGNED_SHORT, 0);
  }

  animate() {
    this.render();
    requestAnimationFrame(() => this.animate());
  }

  setHover(isHovered) {
    this.animationController.setHover(isHovered);
  }

  playOneAnimation(callback) {
    this.animationController.playOneAnimation(callback);
  }

  get isAnimating() {
    return this.animationController.isAnimating;
  }

  get isHovered() {
    return this.animationController.isHovered;
  }

  resize(width, height) {
    this.width = width;
    this.height = height;
    this.gl.viewport(0, 0, width, height);
  }

  dispose() {
    const gl = this.gl;

    if (this.mesh) {
      gl.deleteBuffer(this.mesh.vertexBuffer);
      gl.deleteBuffer(this.mesh.texCoordBuffer);
      gl.deleteBuffer(this.mesh.normalBuffer);
      gl.deleteBuffer(this.mesh.indexBuffer);
    }

    if (this.texture) {
      gl.deleteTexture(this.texture);
    }

    if (this.shaderProgram) {
      gl.deleteProgram(this.shaderProgram.program);
    }

    this.animationController.dispose();
  }
}
